import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from '../core/services/http.service';
import {
  QueryRequest,
  QueryResponse,
  ConversationSummary,
  ConversationsResponse,
  ConversationDetail,
  ClearSessionResponse,
} from '../models';

// Re-export models for consumers that imported them from this file
export type {
  QueryRequest,
  QueryResponse,
  ConversationSummary,
  ConversationDetail,
  ClearSessionResponse,
} from '../models';

// ── Service ──────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class QueryApiService {
  constructor(private httpService: HttpService) {}

  /**
   * POST /api/v1/query
   * Sends a question with a session_id and returns the AI answer.
   */
  query(payload: QueryRequest): Observable<QueryResponse> {
    // Only send question and session_id — backend extracts user_id from the JWT token
    const { user_id, ...cleanPayload } = payload;
    return this.httpService.query(cleanPayload);
  }

  /**
   * GET /api/v1/query/conversations
   * Returns all conversation sessions (for chat history sidebar).
   * Backend returns: { sessions: ConversationSummary[] }
   */
  getConversations(): Observable<ConversationSummary[]> {
    return this.httpService.getConversations();
  }

  /**
   * GET /api/v1/query/conversations/{session_id}
   * Returns full message history for a session (used when clicking a chat history item).
   */
  getConversationById(sessionId: string): Observable<ConversationDetail> {
    return this.httpService.getConversationById(sessionId);
  }

  /**
   * POST /api/v1/query/clear
   * Clears/resets a session on the backend.
   */
  clearSession(sessionId: string): Observable<ClearSessionResponse> {
    return this.httpService.clearSession(sessionId);
  }

  /**
   * POST /api/v1/feedback
   * Submits user feedback for a specific message in a session.
   */
  submitFeedback(feedback: {
    session_id: string;
    message_id: string;
    sentiment: 'Like' | 'Dislike';
    issue_type: 'Data' | 'Graph' | 'Response Summary' | '';
    response_rating: number;
    additional_comments: string;
  }): Observable<any> {
    return this.httpService.submitFeedback(feedback);
  }

  /**
   * POST /api/v1/causal-feedback
   * Submits RCA (Root Cause Analysis) validation feedback for a well event.
   */
  submitRcaFeedback(payload: {
    WellID: string;
    EventID: string;
    Verification: string;
  }): Observable<any> {
    return this.httpService.Post('/api/v1/causal-feedback', payload);
  }

  /** Generate a UUID v4 for new sessions using cryptographically secure random values */
  static generateSessionId(): string {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback using cryptographically secure crypto.getRandomValues
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const arr = new Uint8Array(16);
      crypto.getRandomValues(arr);
      arr[6] = (arr[6] & 0x0f) | 0x40; // Set version to 4
      arr[8] = (arr[8] & 0x3f) | 0x80; // Set variant to RFC 4122
      return Array.from(arr, (byte) => byte.toString(16).padStart(2, '0'))
        .join('')
        .replace(/(.{8})(.{4})(.{4})(.{4})(.{12})/, '$1-$2-$3-$4-$5');
    }
    // Last resort fallback (should rarely occur in modern browsers)
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const randomValue = new Uint32Array(1);
      crypto.getRandomValues(randomValue);
      const r = (randomValue[0] % 16);
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
